import React, { useState, useEffect, useCallback } from 'react';
import {
    ArrowLeft, Calendar, FileText, ChevronRight,
    Loader2, Download, TrendingUp, TrendingDown,
    Users, Handshake, X, Eye
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { GET } from '../services/api';

const MemberData = () => {
    const navigate = useNavigate();

    // --- State: Summary ---
    const [activeFilter, setActiveFilter] = useState('Current');
    const [stats, setStats] = useState(null);
    const [statsLoading, setStatsLoading] = useState(true);

    // --- State: Detail View ---
    const [view, setView] = useState('summary');
    const [activeCategory, setActiveCategory] = useState(null);
    const [detailList, setDetailList] = useState([]);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailPage, setDetailPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);

    // --- State: Image Preview ---
    const [previewImage, setPreviewImage] = useState(null);
    const [failedAvatars, setFailedAvatars] = useState({});

    // --- Mappings ---
    const filterMap = {
        'Current': 'current',
        'Last Month': 'last_month',
        'Tenure': 'tenure',
        'All': 'all'
    };

    const categoryConfig = {
        'business_received': { title: 'Business Received', api: '/app/business-received-detail', type: 'money', icon: TrendingDown },
        'business_given': { title: 'Business Given', api: '/app/business-given-detail', type: 'money', icon: TrendingUp },
        'references_given': { title: 'References Passed', api: '/app/reference-given-detail', type: 'text', icon: Users },
        'references_received': { title: 'References Received', api: '/app/reference-received-detail', type: 'text', icon: Users },
        'one_to_one_meetings': { title: 'One to One Meetings', api: '/app/one-to-one-metting', type: 'meeting', icon: Handshake }
    };

    const formatNumber = (num) => {
        if (!num) return '0';
        const n = Number(num);
        if (isNaN(n)) return num;
        if (n >= 10000000) return (n / 10000000).toFixed(2) + ' Cr';
        if (n >= 100000) return (n / 100000).toFixed(2) + ' L';
        if (n >= 1000) return (n / 1000).toFixed(1) + ' K';
        return n.toLocaleString('en-IN');
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: 'numeric', month: 'short', year: 'numeric'
        });
    };

    // --- Helper: Download Image ---
    const handleDownloadImage = async (imageUrl) => {
        try {
            const response = await fetch(imageUrl);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const fileName = imageUrl.substring(imageUrl.lastIndexOf('/') + 1) || `file-${Date.now()}.png`;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Download failed:", error);
            window.open(imageUrl, '_blank');
        }
    };

    // --- API Calls ---

    // 1. Fetch Summary Stats
    useEffect(() => {
        const fetchStats = async () => {
            setStatsLoading(true);
            try {
                const queryParam = filterMap[activeFilter] || 'all';
                const result = await GET(`/app/data?filter=${queryParam}`);

                if (result && result.success && result.data) {
                    setStats(result.data);
                }
            } catch (error) {
                console.error("Stats Fetch Error:", error);
            } finally {
                setStatsLoading(false);
            }
        };
        fetchStats();
    }, [activeFilter]);

    // 2. Fetch Detail List
    const fetchDetails = useCallback(async (categoryKey, pageNum, isLoadMore = false) => {
        const config = categoryConfig[categoryKey];
        if (!config) return;

        setDetailLoading(true);
        try {
            const queryFilter = filterMap[activeFilter] || 'all';
            const limit = 20;
            const url = `${config.api}?filter=${queryFilter}&page=${pageNum}&limit=${limit}`;

            const result = await GET(url);

            if (result && result.success) {
                const newData = result.data || [];
                if (isLoadMore) {
                    setDetailList(prev => [...prev, ...newData]);
                } else {
                    setDetailList(newData);
                }
                const totalLoaded = (pageNum - 1) * limit + newData.length;
                setHasMore(totalLoaded < result.total);
            }
        } catch (error) {
            console.error("Detail Fetch Error:", error);
        } finally {
            setDetailLoading(false);
        }
    }, [activeFilter]);

    // --- Handlers ---
    const handleCardClick = (key) => {
        setDetailList([]);
        setFailedAvatars({});
        setActiveCategory(key);
        setView('detail');
        setDetailPage(1);
        setHasMore(false);
        fetchDetails(key, 1, false);
        window.scrollTo(0, 0);
    };

    const handleLoadMore = () => {
        const nextPage = detailPage + 1;
        setDetailPage(nextPage);
        fetchDetails(activeCategory, nextPage, true);
    };

    const handleBackToSummary = () => {
        setDetailList([]);
        setFailedAvatars({});
        setActiveCategory(null);
        setView('summary');
    };

    // --- UI Component: Stat Card ---
    const StatCard = ({ item, onClick, index }) => {
        const getIconComponent = () => {
            switch (item.key) {
                case 'business_received': return TrendingDown;
                case 'business_given': return TrendingUp;
                case 'one_to_one_meetings': return Handshake;
                default: return Users;
            }
        };

        const Icon = getIconComponent();
        const isFullWidth = index === 4;

        return (
            <div
                onClick={() => onClick(item.key)}
                className={`
                    relative overflow-hidden rounded-3xl p-5 cursor-pointer transition-all duration-300 transform active:scale-[0.98]
                    ${isFullWidth ? 'col-span-2 bg-gradient-to-r from-[#1A87CF] to-[#4fa3db]' : 'col-span-1 bg-white'}
                    ${!isFullWidth ? 'shadow-[0_4px_20px_rgb(0,0,0,0.05)] border border-slate-50' : 'shadow-lg shadow-blue-200'}
                `}
            >
                {isFullWidth && (
                    <div className="absolute right-[-20px] top-[-20px] opacity-10 rotate-12">
                        <Handshake size={120} color="white" />
                    </div>
                )}

                <div className="flex flex-col h-full justify-between relative z-10">
                    <div className="flex justify-between items-start mb-2">
                        <div className={`
                            w-10 h-10 rounded-2xl flex items-center justify-center
                            ${isFullWidth ? 'bg-white/20 backdrop-blur-sm' : 'bg-blue-50'}
                        `}>
                            <Icon
                                size={20}
                                className={isFullWidth ? "text-white" : "text-[#1A87CF]"}
                            />
                        </div>
                        {!isFullWidth && <ChevronRight size={16} className="text-gray-300" />}
                    </div>

                    <div>
                        <p className={`text-xs font-semibold uppercase tracking-wider mb-1 ${isFullWidth ? 'text-blue-100' : 'text-gray-400'}`}>
                            {item.title} {item.subtitle}
                        </p>
                        <h3 className={`text-2xl font-bold font-sans ${isFullWidth ? 'text-white' : 'text-slate-800'}`}>
                            <span className="text-lg mr-0.5 opacity-70">{item.prefix}</span>
                            {formatNumber(item.value)}
                        </h3>
                    </div>
                </div>
            </div>
        );
    };

    // --- UI Component: Detail Row ---
    const renderDetailItem = (item, index) => {
        if (!activeCategory) return null;

        let name = "Unknown Member";
        let subText = "";
        let initials = "MB";
        const amount = item.amount;
        const remarks = item.remarks;
        const date = item.meeting_date || item.date || item.created_at;

        // Resolve profile picture from any possible member object for all categories.
        const getProfilePicture = () => {
            // In one-to-one records, only show visitor's profile picture if it exists
            if (activeCategory === 'one_to_one_meetings') {
                // Only use visitor profile picture if it exists, otherwise show nothing
                if (item?.visitor?.profile_picture && typeof item.visitor.profile_picture === 'string' && item.visitor.profile_picture.trim()) {
                    return item.visitor.profile_picture;
                }
                // If visitor doesn't have picture, return null (show initials instead)
                return null;
            }

            const candidates = [
                item?.visitor?.profile_picture,
                item?.profile_picture,
                item?.from_member?.profile_picture,
                item?.from_member_id?.profile_picture,
                item?.to_member?.profile_picture,
                item?.to_member_id?.profile_picture,
                item?.from_business?.profile_picture,
                item?.to_business?.profile_picture,
                item?.host?.profile_picture,
                item?.member?.profile_picture,
                item?.user?.profile_picture
            ];

            const validSrc = candidates.find((src) => typeof src === 'string' && src.trim());
            return validSrc || null;
        };

        if (activeCategory === 'one_to_one_meetings') {
            if (item.visitor && item.visitor.full_name) {
                name = item.visitor.full_name;
                subText = item.visitor.chapter_name || "Visitor";
            } else if (item.host && item.host.first_name) {
                name = `${item.host.first_name} ${item.host.last_name}`;
                subText = "Host";
            }
        } else if (activeCategory === 'references_given') {
            if (item.to_business) {
                name = item.to_business.full_name || "Unknown Member";
                subText = item.to_business.business_name || "Member";
            }
        }
        else if (activeCategory === 'references_received') {
            if (item.from_business) {
                name = item.from_business.full_name || "Unknown Member";
                subText = item.from_business.business_name || "Member";
            }
        }
        else {
            if (item.to_member || item.to_member_id) {
                const tm = item.to_member || item.to_member_id;
                if (tm.full_name) { name = tm.full_name; subText = tm.business_name; }
            }
            if (item.from_member || item.from_member_id) {
                const fm = item.from_member || item.from_member_id;
                if (fm.full_name || fm.first_name) {
                    name = fm.full_name || `${fm.first_name} ${fm.last_name}`;
                    subText = fm.business_name || "";
                }
            }
        }

        initials = name ? name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() : "NA";
        const uniqueKey = item._id ? `${item._id}-${index}` : `item-${index}`;
        const profilePicture = getProfilePicture();
        const showProfilePicture = profilePicture && !failedAvatars[uniqueKey];

        return (
            <div key={uniqueKey} className="bg-white p-4 rounded-2xl mb-3 shadow-[0_2px_8px_rgb(0,0,0,0.04)] border border-slate-50">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3.5 overflow-hidden">
                        {/* Avatar */}
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center flex-shrink-0 text-[#1A87CF] font-bold text-sm border-2 border-white shadow-sm overflow-hidden">
                            {showProfilePicture ? (
                                <img
                                    src={profilePicture}
                                    alt={name}
                                    className="w-full h-full object-cover"
                                    onError={() => {
                                        setFailedAvatars((prev) => ({ ...prev, [uniqueKey]: true }));
                                    }}
                                />
                            ) : (
                                initials
                            )}
                        </div>

                        {/* Info */}
                        <div className="min-w-0">
                            <h4 className="font-bold text-slate-800 text-sm truncate capitalize">{name}</h4>
                            <p className="text-xs text-slate-500 truncate capitalize">{subText || 'Member'}</p>
                            {date && (
                                <div className="flex items-center gap-1 mt-1">
                                    <Calendar size={10} className="text-gray-400" />
                                    <span className="text-[10px] text-gray-400">{formatDate(date)}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right Side: Amount or View Image Button */}
                    <div className="text-right flex-shrink-0 ml-2">
                        {amount ? (
                            <span className="block text-[#1A87CF] font-bold text-base">
                                ₹{Number(amount).toLocaleString('en-IN')}
                            </span>
                        ) : activeCategory === 'one_to_one_meetings' && item.image ? (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    // Use Direct Image URL from API
                                    setPreviewImage(item.image);
                                }}
                                className="w-9 h-9 rounded-full bg-blue-50 flex items-center justify-center text-[#1A87CF] hover:bg-[#1A87CF] hover:text-white transition-colors"
                                title="View Photo"
                            >
                                <Eye size={18} />
                            </button>
                        ) : null}
                    </div>
                </div>

                {remarks && (
                    <div className="mt-3 text-xs text-slate-500 bg-slate-50 p-2.5 rounded-lg border border-slate-100 flex gap-2">
                        <FileText size={12} className="mt-0.5 flex-shrink-0 opacity-50" />
                        <span className="italic">"{remarks}"</span>
                    </div>
                )}
            </div>
        );
    };

    // --- VIEW 1: DETAIL LIST ---
    if (view === 'detail') {
        const config = categoryConfig[activeCategory] || {};
        const Icon = config.icon || FileText;

        return (
            <div className="min-h-screen bg-[#F8FAFC] pb-36 relative">
                {/* Global Styles for Scrollbar */}
                <style>{`
                    .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
                    .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 8px; }
                    .custom-scrollbar::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 8px; }
                    .custom-scrollbar::-webkit-scrollbar-thumb:hover { background-color: #94a3b8; }
                `}</style>

                {/* Sticky Header */}
                <div className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-slate-100 px-4 pt-4 pb-3">
                    <div className="flex items-center gap-3 max-w-lg mx-auto">
                        <button
                            onClick={handleBackToSummary}
                            className="w-10 h-10 rounded-full bg-white shadow-sm border border-slate-100 flex items-center justify-center text-slate-700 hover:text-[#1A87CF] active:bg-gray-50"
                        >
                            <ArrowLeft size={20} />
                        </button>
                        <div className="flex-1">
                            <h1 className="text-lg font-bold text-slate-800">{config.title}</h1>
                            <p className="text-xs text-slate-500">{detailList.length} records found</p>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="container mx-auto px-4 max-w-lg mt-4">
                    {detailList.length > 0 ? (
                        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                            {detailList.map((item, index) => renderDetailItem(item, index))}
                        </div>
                    ) : (
                        !detailLoading && (
                            <div className="flex flex-col items-center justify-center py-20 opacity-60">
                                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                                    <Icon size={32} className="text-gray-400" />
                                </div>
                                <p className="text-slate-500 font-medium">No records found</p>
                                <p className="text-xs text-slate-400 mt-1">Try changing the filter</p>
                            </div>
                        )
                    )}

                    {detailLoading && (
                        <div className="flex justify-center py-8">
                            <Loader2 className="animate-spin text-[#1A87CF]" size={32} />
                        </div>
                    )}

                    {!detailLoading && hasMore && (
                        <div className="flex justify-center mt-4 mb-8">
                            <button
                                onClick={handleLoadMore}
                                className="px-6 py-3 bg-white text-[#1A87CF] text-sm font-semibold rounded-full shadow-lg shadow-blue-100 border border-blue-100 active:scale-95 transition-all"
                            >
                                Load More
                            </button>
                        </div>
                    )}
                </div>

                {/* IMAGE PREVIEW MODAL */}
                {previewImage && (
                    <div className="fixed inset-0 z-[60] bg-black/95 backdrop-blur-md flex flex-col items-center justify-center p-4 animate-in fade-in duration-200">
                        <button
                            onClick={() => setPreviewImage(null)}
                            className="absolute top-6 right-6 p-2 bg-white/10 rounded-full text-white hover:bg-white/20 transition-colors backdrop-blur-md z-50"
                        >
                            <X size={28} />
                        </button>

                        <div className="relative w-full max-w-lg max-h-[80vh] flex items-center justify-center" onClick={e => e.stopPropagation()}>
                            <img
                                src={previewImage}
                                alt="Meeting Proof"
                                className="max-w-full max-h-[70vh] rounded-xl shadow-2xl object-contain"
                            />
                        </div>

                        <div className="mt-8">
                            <button
                                onClick={() => handleDownloadImage(previewImage)}
                                className="flex items-center gap-3 px-8 py-3.5 bg-white text-black rounded-full font-bold shadow-[0_0_20px_rgba(255,255,255,0.3)] hover:scale-105 active:scale-95 transition-all"
                            >
                                <Download size={20} /> Download Photo
                            </button>
                        </div>
                    </div>
                )}

            </div>
        );
    }

    // --- VIEW 2: DASHBOARD (Summary) ---
    const cardsConfig = [
        { key: 'business_received', title: 'Business', subtitle: 'Received', value: stats?.business_received, prefix: '₹' },
        { key: 'business_given', title: 'Business', subtitle: 'Given', value: stats?.business_given, prefix: '₹' },
        { key: 'references_received', title: 'Refs', subtitle: 'Received', value: stats?.references_received, prefix: '' },
        { key: 'references_given', title: 'Refs', subtitle: 'Passed', value: stats?.references_given, prefix: '' },
        { key: 'one_to_one_meetings', title: 'One To One', subtitle: 'Meetings', value: stats?.one_to_one_meetings, prefix: '' }
    ];

    return (
        <div className="min-h-screen bg-[#F8FAFC] pb-36 font-sans selection:bg-blue-100">
            {/* Header Area */}
            <div className="bg-white pb-6 pt-6 px-5 rounded-b-[2.5rem] shadow-[0_10px_40px_-10px_rgba(0,0,0,0.05)] mb-6">
                <div className="max-w-lg mx-auto">
                    {/* Top Nav */}
                    <div className="flex justify-between items-center mb-6">
                        <button
                            onClick={() => navigate(-1)}
                            className="p-2 -ml-2 text-slate-600 hover:text-[#1A87CF] transition-colors"
                        >
                            <ArrowLeft size={24} />
                        </button>
                        <div></div>
                    </div>

                    {/* Title */}
                    <div className="mb-6">
                        <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight">Performance</h1>
                        <p className="text-slate-500 text-sm mt-1">Track your business growth</p>
                    </div>

                    {/* Filters */}
                    <div className="bg-slate-100 p-1.5 rounded-xl flex relative overflow-hidden">
                        {Object.keys(filterMap).map((filter) => (
                            <button
                                key={filter}
                                onClick={() => setActiveFilter(filter)}
                                className={`flex-1 py-2 rounded-lg text-xs font-semibold text-center transition-all duration-300 relative z-10 
                                    ${activeFilter === filter
                                        ? 'bg-white text-[#1A87CF] shadow-sm'
                                        : 'text-slate-500 hover:text-slate-700'
                                    }`}
                            >
                                {filter}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Content Area */}
            <div className="container mx-auto px-5 max-w-lg">
                {statsLoading ? (
                    <div className="grid grid-cols-2 gap-4">
                        {[1, 2, 3, 4, 5].map((i) => (
                            <div key={i} className={`h-36 bg-white rounded-3xl animate-pulse ${i === 5 ? 'col-span-2' : ''}`}></div>
                        ))}
                    </div>
                ) : (
                    <div className="grid grid-cols-2 gap-4">
                        {cardsConfig.map((stat, index) => (
                            <StatCard
                                key={stat.key}
                                item={stat}
                                onClick={handleCardClick}
                                index={index}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MemberData;